import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Define interface for the cached service information
interface CachedServiceInfo {
  id: string;
  name: string;
  lastFetched: Date;
}

export const useVendorServiceCache = () => {
  const { user, isVendor } = useAuth();
  const [serviceInfo, setServiceInfo] = useState<CachedServiceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check localStorage for cached service info first
  useEffect(() => {
    const loadCachedServiceInfo = () => {
      if (!user || !isVendor) {
        setServiceInfo(null);
        setLoading(false);
        return;
      }

      try {
        // Try to load from localStorage first
        const cachedInfoStr = localStorage.getItem(`vendor_service_${user.id}`);
        if (cachedInfoStr) {
          const cachedInfo = JSON.parse(cachedInfoStr);
          const lastFetched = new Date(cachedInfo.lastFetched);
          const cacheAge = Date.now() - lastFetched.getTime();
          
          // If cache is less than 5 minutes old, use it
          if (cacheAge < 5 * 60 * 1000) {
            console.log("[useVendorServiceCache] Using cached service info");
            setServiceInfo({
              ...cachedInfo,
              lastFetched: new Date(cachedInfo.lastFetched)
            });
            setLoading(false);
            return;
          }
        }
        
        // Otherwise, fetch from database
        fetchServiceInfo();
      } catch (err) {
        console.error("[useVendorServiceCache] Error loading cache:", err);
        fetchServiceInfo();
      }
    };

    loadCachedServiceInfo();
  }, [user, isVendor]);

  const fetchServiceInfo = async () => {
    if (!user || !isVendor) {
      setServiceInfo(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log("[useVendorServiceCache] Fetching service info for user:", user.id);
      
      const { data: services, error } = await supabase
        .from('services')
        .select('id, name')
        .eq('owner_id', user.id)
        .limit(1);

      if (error) throw error;

      if (services && services.length > 0) {
        const newServiceInfo = {
          id: services[0].id.toString(),
          name: services[0].name || 'Your Service',
          lastFetched: new Date()
        };
        
        setServiceInfo(newServiceInfo);
        
        // Cache the service info in localStorage
        try {
          localStorage.setItem(
            `vendor_service_${user.id}`, 
            JSON.stringify(newServiceInfo)
          );
        } catch (err) {
          console.error("[useVendorServiceCache] Error caching service info:", err);
        }
      } else {
        setServiceInfo(null);
      }
    } catch (err: any) {
      console.error("[useVendorServiceCache] Error fetching service:", err);
      setError(err.message || "Failed to load service information");
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    if (user) {
      localStorage.removeItem(`vendor_service_${user.id}`);
    }
    setServiceInfo(null);
  };

  return { 
    serviceInfo, 
    loading, 
    error,
    refetch: fetchServiceInfo,
    clearCache
  };
};
